import { clsx } from 'clsx';
import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { Icon } from '@/app/kit';

/**
 * One row in a list: leading (Avatar, RatingBadge), title, subtitle, trailing. 56 px tall at
 * least. With `onPress` it is a button, with `to` a router link, otherwise plain.
 *
 *   <ul className="flex flex-col gap-2">
 *     <ListRow as="li" leading={<Avatar name="Giorgos" />} title="Giorgos" subtitle="12 games" to="/p/1" />
 *   </ul>
 */
export interface ListRowProps {
  title: ReactNode;
  subtitle?: ReactNode;
  leading?: ReactNode;
  trailing?: ReactNode;
  onPress?: () => void;
  to?: string;
  /** Shows a chevron when the row navigates. Default: true with `to` or `onPress`. */
  chevron?: boolean;
  as?: 'div' | 'li';
  className?: string;
}

export function ListRow({
  title,
  subtitle,
  leading,
  trailing,
  onPress,
  to,
  chevron,
  as: As = 'div',
  className,
}: ListRowProps) {
  const showChevron = chevron ?? Boolean(onPress || to);
  const inner = (
    <>
      {leading && <span className="shrink-0">{leading}</span>}
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate font-bold leading-tight">{title}</span>
        {subtitle && <span className="truncate text-sm text-ink/75">{subtitle}</span>}
      </span>
      {trailing && <span className="shrink-0">{trailing}</span>}
      {showChevron && <Icon name="chevron" size={20} className="shrink-0" />}
    </>
  );
  const rowCls =
    'flex min-h-14 w-full items-center gap-3 rounded-tts border-(length:--tts-border) border-ink bg-white px-3 py-2 text-left text-ink shadow-hard-sm';
  if (to) {
    return (
      <As className={className}>
        <Link to={to} className={clsx(rowCls, 'tts-press')}>
          {inner}
        </Link>
      </As>
    );
  }
  if (onPress) {
    return (
      <As className={className}>
        <button type="button" onClick={onPress} className={clsx(rowCls, 'tts-press')}>
          {inner}
        </button>
      </As>
    );
  }
  return <As className={clsx(rowCls, className)}>{inner}</As>;
}
